import React from "react";
import classnames from "classnames";
import HeaderValue from "./HeaderValue";
import "./Table.scss";
import { useFixed, useFixedWidth } from "./hooks";

import Loader from "../Loader/Loader";

const renderHeaderCellDefault = ({
  columnInfo,
  sortBy,
  sortOrder,
  onSortChange
}) => {
  return (
    <th
      className={columnInfo.className}
      colSpan={columnInfo.colSpan}
      key={columnInfo.accessor || columnInfo._key}
    >
      <HeaderValue
        sortBy={sortBy}
        sortOrder={sortOrder}
        accessor={columnInfo.accessor}
        onSortChange={onSortChange}
      >
        {columnInfo.renderHeaderCell
          ? columnInfo.renderHeaderCell({})
          : columnInfo.title}
      </HeaderValue>
    </th>
  );
};

const renderHeaderRowDefault = ({
  columnRow,
  sortBy,
  sortOrder,
  onSortChange
}) => {
  return (
    <tr>
      {columnRow.map(columnInfo => {
        return renderHeaderCellDefault({
          columnInfo,
          sortBy,
          sortOrder,
          onSortChange
        });
      })}
    </tr>
  );
};

const renderDataCellDefault = ({ columnInfo, data }) => {
  return (
    <td className={columnInfo.dataClassName} key={columnInfo.accessor}>
      {columnInfo.renderDataCell
        ? columnInfo.renderDataCell({ columnInfo, data })
        : data[columnInfo.accessor]}
    </td>
  );
};

const renderDataRowDefault = ({ columns, data, i, trProps = {} }) => {
  return (
    <tr key={i} {...trProps}>
      {columns.map(columnInfo => {
        return renderDataCellDefault({ columnInfo, data });
      })}
    </tr>
  );
};

const renderSummaryRowDefault = ({ summaryColumns, summary }) => {
  return (
    <tr className="table__summary">
      {summaryColumns.map((summaryInfo, i) => {
        return renderSummaryCellDefault({ summaryInfo, summary, i });
      })}
    </tr>
  );
};

const renderSummaryCellDefault = ({ summaryInfo, summary, i }) => {
  return (
    <td
      className={summaryInfo.className}
      colSpan={summaryInfo.colSpan}
      key={i}
      style={{ textAlign: summaryInfo.align ?? "right" }}
    >
      {summaryInfo.renderCell
        ? summaryInfo.renderCell({ summaryInfo, summary })
        : summaryInfo.title
        ? summaryInfo.title
        : summary[summaryInfo.accessor]}
    </td>
  );
};

/*
function convertSummary(columns, summary) {
    let offset = 0;

    const results = [];

    Object.keys(summary).forEach(key => {
        const index = columns.findIndex(i => {
            return i.accessor == key;
        });

        results.push({
            colSpan: index - offset,
            value: summary[key],
            className: columns[index].summaryClassName
        });

        offset = index;
    });

    return results;
}
*/

function sort(sortBy, sortOrder, data) {
  if (!sortBy && !sortOrder) {
    return data;
  }

  const newData = data.slice();

  newData.sort((a, b) => {
    if (sortOrder == "asc") {
      return a[sortBy] - b[sortBy];
    } else {
      return b[sortBy] - a[sortBy];
    }
  });

  return newData;
}

const getTrPropsDefault = ({}) => {
  return {};
};

const Table = ({
  sortBy,
  sortOrder,

  className,

  columns,
  extraColumns,
  data,
  summaryColumns,
  summary,

  renderHeaderRow,
  renderExtraHeaderRow,
  renderSummaryRow,
  renderDataRow,

  getTrProps,

  onSortChange,

  loading
}) => {
  const [fixed, setFixed, headRef, style] = useFixed();
  useFixedWidth(loading, headRef);

  const sortedData = sort(sortBy, sortOrder, data);

  return (
    <table
      className={classnames("table", className, { "fixed-fix": fixed })}
      ref={headRef}
    >
      <thead className={classnames({ "fixed-thead": fixed })} style={style}>
        {extraColumns && renderExtraHeaderRow({ columnRow: extraColumns })}
        {renderHeaderRow({
          columnRow: columns,
          sortBy,
          sortOrder,
          onSortChange
        })}
      </thead>

      <tbody>
        {loading ? (
          <tr>
            <td colSpan={columns.length}>
              <Loader />
            </td>
          </tr>
        ) : (
          <React.Fragment>
            {summaryColumns.length != 0 &&
              renderSummaryRow({ summaryColumns, summary })}
            {sortedData.map((data, i) => {
              const trProps = getTrProps({ columns, data, i });
              return renderDataRow({ columns, data, i, trProps });
            })}
          </React.Fragment>
        )}
      </tbody>
    </table>
  );
};

Table.defaultProps = {
  // рендер главной строки в thead, в которой указаны названия колонок и сортировки
  renderHeaderRow: renderHeaderRowDefault,

  // рендер дополнительной строки в thead
  renderExtraHeaderRow: renderHeaderRowDefault,

  // рендер строки summary в tbody
  renderSummaryRow: renderSummaryRowDefault,

  // рендер строки tbody
  renderDataRow: renderDataRowDefault,

  // возможность задать кастомные свойства для tr в tbody
  getTrProps: getTrPropsDefault,

  // колбек на изменение сортировки
  onSortChange: ({ sortBy, sortOrder }) => {},

  columns: [],
  data: [],

  /*summaryColumns - колонки которые нужно вывести в строке summary,
    пример:

      [
          {
              title: "Итого",
              className: "table__border-right",
              accessor: "teasers",
              renderCell: ({ summary }) => (),
              colSpan: 6,
          }
      ]
     */
  summaryColumns: [],
  //обычный dict
  summary: {}
};

export default Table;
